import React, { Component, useEffect, useState } from "react";
import Amplify, {Auth, Hub, Cache } from "aws-amplify";
import { connect } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import { bindActionCreators } from "redux";
import { toast } from 'react-toastify';
// Auth
import {
  loginSuccess,
  cognitoLoginSuccess,
  cognitoLoginFail,
  logout,
} from "../../actions/auth";
import { getUserInfo } from "../../actions/settings";
// API Urls
import APIUrls from "../../constants/ApiUrls";
import { APIManager } from "../../utils/api";
import LoaderButton from "../../components/LoaderButton";
import "./ConfirmVerification.scss";
import { codeAndParamsFromUrl, addEventNameToEndpoint } from "../../utils/common";
import { isOnboarding } from "../../utils/userState";

import imgSignupInvited from '../../assets/images/ilustration-signup-invited.png';
import imgCamelLogo from '../../assets/images/content-camel-logo.png';
import logoSSOMicrosoft from '../../assets/images/logo-sso-microsoft.svg';
import "./SSO.scss";


const authSettings = {
  mandatorySignIn: false,
  region: "us-west-2",
  userPoolId: "",
  identityPoolId: "",
  userPoolWebClientId: "",
  oauth: {
    domain: "",
    // scope: ["email", "openid", "aws.cognito.signin.user.admin", "profile"], // remove scopes per https://github.com/aws-amplify/amplify-js/issues/5935
    redirectSignIn: APIUrls.clientLoginSSO,
    redirectSignOut: APIUrls.clientRoot,
    responseType: "code"
  }
};

const analyticsSettings = {
  disabled: true,
  autoSessionRecord: true,
  AWSPinpoint: {
    appId: process.env.REACT_APP_PINPOINT_ID, 
    region: 'us-east-1',
    endpoint: {
      attributes: {
      },
      channelType: "EMAIL",
      optOut: 'NONE',
      userId: "",
      userAttributes: {
      }
    },
  }
};

const FormType = {
  UNKNOWN: "unknown",
  EMAIL_LOOKUP: "emailLookup",
  OKTA: "Okta",
  AZUREAD: "AzureAD",
};

const ERROR_USER_EXISTS = "Already+found+an+entry+for+username"; // the error when a SAML user exists, but it's actually OK

const configureAnalytics = async (user) => {
  const { userId, tenantId, email, companyName, emailDomain, role } = user;
  
  analyticsSettings.disabled = false;
  analyticsSettings.AWSPinpoint.endpointId = userId;
  analyticsSettings.AWSPinpoint.endpoint.address = email;
  analyticsSettings.AWSPinpoint.endpoint.attributes.tenantId = [tenantId];
  analyticsSettings.AWSPinpoint.endpoint.attributes.eventName = analyticsSettings.AWSPinpoint.endpoint.attributes.eventName || [];
  analyticsSettings.AWSPinpoint.endpoint.userId = userId;
  analyticsSettings.AWSPinpoint.endpoint.userAttributes.tenantId = [tenantId];
  analyticsSettings.AWSPinpoint.endpoint.userAttributes.userId = [userId];
  analyticsSettings.AWSPinpoint.endpoint.userAttributes.companyName = [companyName];
  analyticsSettings.AWSPinpoint.endpoint.userAttributes.emailDomain = [emailDomain];
  analyticsSettings.AWSPinpoint.endpoint.userAttributes.role = [role];
  // not sure if we can configure just Analytics at this point
  Amplify.Analytics.configure(analyticsSettings);
  Amplify.Analytics.autoTrack('session', {
    enable: true,
    attributes: {
      tenantId,
      userId
    },
  });

  Amplify.Analytics.autoTrack('pageView', {
    enable: true,
    eventName: 'user-page-view',
    attributes: {
      tenantId,
      userId
    },
    type: 'SPA',
    getUrl: () => {
      return window.location.origin + window.location.pathname;
    }
  });

  Amplify.Analytics.autoTrack('event', {
    enable: true,
    events: ['click'],
    selectorPrefix: 'data-cc-analytics-',
    provider: 'AWSPinpoint',
    attributes: {
      tenantId,
      userId
    }
  });

  Amplify.Analytics.enable();
};

const SSO = props => {
  const [tenant, setTenant] = useState(props.tenant);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [formType, setFormType] = useState(FormType.UNKNOWN);
  const [email, setEmail] = useState(undefined);
  const location = useLocation();

  const getToken = async () => {
    return await Auth.currentSession()
    .then(session => session)
    .catch(err => console.error(err));
  }

  const getUser = async () => {
    return await Auth.currentAuthenticatedUser()
    .then(user => user)
    .catch(err => console.error(err));
  }

  useEffect(() => {

    const setupHubListener = async () => {
      await listenWithAuthHub();
    }

    setupHubListener();
    parseParams();
    setupCognito();
    try {
      const currentUser = Auth.currentAuthenticatedUser().catch(error => { console.log({ tenant, error: error.toString() }) });;
    } catch (error) {
      console.error(error);
    }
    
  },[]);

  /**
   * Set up Cognito based on the user's email
   */
   useEffect(() => {
    setupCognito();
    setForm();
    if (tenant) {
      try {
        Auth.currentAuthenticatedUser().then(user => { 
          props.cognitoLoginSuccess(user);
          Auth.currentSession().then(session => {
          });
          if (isOnboarding(user)) {
            props.history.push('/onboarding');
          } else {
            props.history.push('/dashboard');
          }
        }).catch(error => { console.error({ tenant, error: error.toString() }) });
      } catch (error) {
        console.error({functionName:"useEffect", info: "currentAuthenticatedUser", error: error.toString()});
      }
    }
  }, [tenant]);
 

  const listenWithAuthHub = async () => {
    Hub.listen("auth", async ({payload: {event, data}}) => {
      switch (event) {
        case "signIn":
        case "cognitoHostedUI":
          await handleSuccessfulAuth(data);
          break;
        case "signOut":
          setToken(null);
          break;
        case "signIn_failure":
        case "cognitoHostedUI_failure":
          console.error("Sign in failure", data);
          if (data && data.message && data.message.includes(ERROR_USER_EXISTS)) {
            // console.log("User exists, but it's OK");
            await handleErrorAndAuthAgain();
          }
          break;
      }
    });
  }
  /**
   * Parse out the cognito params from the url
   * These params are stringified and encoded
   */
  const parseParams = () => {
    const functionName = "parseParams"; 

    try {
      const urlParams = new URLSearchParams(location.search);
      const tenantParams = urlParams.get("tenant");
      const authCodeParams = urlParams.get("code");
      const errorParams = urlParams.get("error");
      if (tenantParams) {
        setupTenant(tenantParams);
      }
      if (errorParams) {
        console.error({functionName, errorParams});
      }
    } catch (error) {
      console.error({functionName, location, error: error.toString()});
      throw new Error(error);
    }
  };

  /**
   * Grab the tenant information and cognito/sso info given the user's email
   * Note: the user must already be registered for an account
   * @param {String} email 
   */
  const setupCognito = async () => {
    
    const federatedInfo = await Cache.getItem('federatedInfo');

    if (tenant) {
      // const theTenant = JSON.parse(tenantData);
      const region = tenant.region ? tenant.region : "us-west-2";
      authSettings.userPoolId = tenant.userPoolId;
      authSettings.identityPoolId = tenant.identityPoolId;
      authSettings.userPoolWebClientId = tenant.appClientId;
      authSettings.oauth.domain = tenant.appClientDomain;
  
      Amplify.configure({
        Auth: authSettings,
      });
    }
  }

  const handleEmailLookup = async (event) => {
    const functionName = "handleEmailLookup";
    event.preventDefault();
    setIsLoading(true);
    
    try {
      const email = event.target.email.value;
      // lookup tenant for given email (we have to make emails URI safe)
      const data = await APIManager.Instance.call("GET", APIUrls.getTenant(email));
      const { tenant } = data; 
      setTenant(tenant);
    } catch (error) {
      console.error({functionName, event, error: error.toString()});
      throw new Error(error);
    }
  };

  const setupTenant = async (tenantId) => {
    const functionName = "setupTenant";
    setIsLoading(true);
    try {
      const data = await APIManager.Instance.call("GET", APIUrls.getTenantById(tenantId));
      const { tenant } = data; 
      // window.localStorage.setItem('cc_tenant_data', JSON.stringify(tenant));
      props.loginSuccess(tenant); // weird legacy name, but this sets the tenant
      setTenant(tenant);
    } catch (error) {
      // window.localStorage.removeItem('cc_tenant_data');
      console.error({functionName, tenantId, error: error.toString()});
      throw new Error(error);
    }
  };


  // /////////////////////////////////////////////////////////////////////////////////
  // Authentication

  const handleErrorAndAuthAgain = async () => {
    await authWithAzure();
  }

  const handleSuccessfulAuth = async (authUser) => {
    try {
      const theUser = await Auth.currentAuthenticatedUser();
      if (theUser && !isAuthenticated) {
        setIsAuthenticated(true);
        props.setUser(theUser);
        props.cognitoLoginSuccess(theUser);
        props.getUserInfo();
        
        if (isOnboarding(theUser)) {
          props.history.push('/onboarding');
        } else {
          props.history.push('/dashboard');
        }
      }
    } catch (error) {
      console.error({functionName: "handleSuccessfulAuth", error: error.toString()});
      props.cognitoLoginFail();

      toast.error('Login error: ' + error, {
        position: toast.POSITION.TOP_RIGHT
      });
    } 
  }

  // /////////////////////////////////////////////////////////////////////////////////
  // Forms

  const setForm = () => {
    if (tenant && tenant.sso) {
      if (tenant.sso === FormType.OKTA) {
        setFormType(FormType.OKTA);
      }
      if (tenant.sso === FormType.AZUREAD) {
        setFormType(FormType.AZUREAD);
      }
    }
  };

  const renderInitialForm = () => {
    return (
      <div className="login-form">
        <LoaderButton
          block
          size="sm"
          type="submit"
          isLoading={isLoading}
          text="Sign In"
          loadingText="Looking up account…"
          className="btn-danger"
        /> 
        <div>
        <Link to="/login">Sign in without SSO {">"}</Link><br />
        </div>
      </div>
    );
  };

  const renderEmailLookupForm = () => {
    return (
      <Form className="signup-form" onSubmit={handleEmailLookup}>
      
        <Form.Group controlId="email" size="sm">
          <Form.Label>Work Email</Form.Label>
          <Form.Control
            autoFocus
            type="email"
            name="email"
            placeholder="yourwork@email.com"
            value={email}
          />
        </Form.Group>
        <LoaderButton
          block
          size="sm"
          type="submit"
          isLoading={isLoading}
          text="Sign In"
          loadingText="Signing In…"
          className="btn-danger"
        /> 
        <div>
        <Link to="/login">Sign in without SSO {">"}</Link><br />
        </div>
      </Form>
    );
  };

  const renderOktaForm = () => {
    return (
      <div>
        <p>Ok! It looks like you're using <strong>Okta</strong></p><br/>
        <Button
          variant="okta"
          onClick={authWithOkta}
        >
          Sign In with Okta
        </Button> 
        <div> 
          <br/>
        <Link to="/login">Sign in without SSO {">"}</Link><br />
        </div>
      </div>
    );
  };

  const renderAzureADForm = () => {
    return (
      <div>
        <p>Ok! It looks like you're using <strong>Microsoft</strong></p><br/>
        <Button
          variant="microsoft"
          onClick={authWithAzure}
        >
           Sign in with Microsoft
        </Button> 
        <div> 
          <br/>
        <Link to="/login">Sign in without SSO {">"}</Link><br />
        </div>
      </div>
    );
  };

  const authWithOkta = async () => {
    try {
      await listenWithAuthHub();
      Auth.federatedSignIn({provider: "Okta"});
    } catch (error) {
      console.error({functionName: "authWithOkta", error: error.toString()});
    }
  };

  const authWithAzure = async () => {
    try {
      await listenWithAuthHub();
      Auth.federatedSignIn({provider: "Microsoft"});
    } catch (error) {
      console.error({functionName: "authWithAzure", error: error.toString()});
    }
  };
  
  const renderFormByType = () => {

    switch (formType) {
      case FormType.EMAIL_LOOKUP:
        return renderEmailLookupForm();
      case FormType.OKTA:
        return renderOktaForm();
      case FormType.AZUREAD:
        return renderAzureADForm();
      case FormType.UNKNOWN:
      default:
        return renderInitialForm();
    }
  }

  return (
    <div className="Signup">
        <div className="left-panel"> 
          <img src={imgSignupInvited} alt="signup to Content Camel" />
        </div>
        <div className="right-panel">
          <img className="camel-logo" src={imgCamelLogo} alt="content camel logo"/>
            <div className="signup-wrapper">
              <div>
                <div className="signup-title">
                  <h3>Sign on with your authentication provider 🔑.</h3>
                </div>
              </div>
              {renderFormByType()}
            </div>
          {/* {error && <p className="error">{error}</p>} */}
        </div>
      </div>
  );
};

const mapStateToProps = state => ({
  tenant: state.auth.tenant,
  cognitoUser: state.auth.cognitoUser,
});

const mapDispatchToProps = dispatch => 
  bindActionCreators({
    getUserInfo,
    loginSuccess,
    cognitoLoginSuccess,
    cognitoLoginFail,
  }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(SSO);
